import { useRef } from 'react'
import { useMove } from '@use-gesture/react'
import { css, useTheme } from '@emotion/react'
import { useSprings, animated, config } from 'react-spring'

import { clamp, normalize } from '@/lib/utils'

type Props = {
  children: React.ReactNode
  id?: string
}

export function HeroContainer({ children, id }: Props) {
  const imageRef = useRef<SVGSVGElement>(null)
  const theme = useTheme()

  const [springs, springApi] = useSprings(6, () => {
    return {
      transform: 'translate3d(0px, 0px, 0px)',
      config: config.slow,
    }
  })

  const bind = useMove(({ xy: [x, y] }) => {
    if (!imageRef.current) return

    const { top, left, bottom, right } =
      imageRef.current.getBoundingClientRect()

    const _x = clamp(x, left, right)
    const _y = clamp(y, top, bottom)

    const dx = normalize(_x, left, right)
    const dy = normalize(_y, top, bottom)

    springApi.start((i) => {
      const _i = 4 - i

      return {
        transform: `translate3d(${_i * dx * 50}px, ${_i * dy * 50}px, 0px)`,
      }
    })
  })

  return (
    <section
      {...bind()}
      id={id}
      css={theme.mq({
        ...theme.layerStyles.fullViewPortHeight,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '--sizes-full',
        backgroundImage:
          'linear-gradient(180deg, #CCECE9 5.98%, rgba(220, 239, 237, 0) 63.02%)',
      })}
    >
      <div
        css={theme.mq({
          ...theme.layerStyles.landingSectionContainer,
          textAlign: {
            base: 'center',
            md: 'left',
          },
          alignItems: {
            base: 'stretch',
            md: 'flex-start',
          },
          display: 'grid',
          gridTemplateColumns: {
            base: '1fr',
            md: '0.52fr 0.48fr',
          },
          gridGap: '--space-6',
          '& > svg': {
            gridRow: 1,
            gridColumn: {
              base: 1,
              md: 2,
            },
            transform: {
              base: 'translate3d(5%, 0, 0)',
              md: 'scale(0.95) translate3d(15%, 0, 0) scale(1.5)',
            },
            zIndex: 0,
          },
        })}
      >
        {children}
        <svg
          ref={imageRef}
          width="100%"
          height="100%"
          viewBox="-200 -200 3288 2900"
          css={css`
            fill-rule: evenodd;
            clip-rule: evenodd;
            stroke-linejoin: round;
            stroke-miterlimit: 2;
          `}
        >
          <animated.g id="behind-1" style={springs[5]}>
            <path
              d="M198.389,964.963c-86.432,-36.167 -177.185,-142.305 -193.722,-299.463c-0.7,-7.079 1.398,-14.15 5.844,-19.7c4.447,-5.554 10.891,-9.15 17.954,-10.017c13.899,-1.62 26.718,-8.291 36.023,-18.737c9.305,-10.446 14.448,-23.946 14.452,-37.929c0,-13.421 -3.341,-37.609 -6.741,-43.942c-1.688,-3.167 -2.729,-6.637 -3.062,-10.208c-0.334,-3.575 0.046,-7.179 1.119,-10.6c1.072,-3.425 2.815,-6.604 5.127,-9.346c2.312,-2.746 5.147,-5.004 8.34,-6.646c38.752,-19.854 81.659,-30.25 125.206,-30.342c43.547,-0.091 86.496,10.13 125.33,29.825c3.21,1.646 6.06,3.913 8.383,6.671c2.322,2.759 4.071,5.954 5.144,9.396c1.073,3.442 1.449,7.067 1.105,10.654c-0.344,3.588 -1.401,7.075 -3.108,10.25c-3.4,6.338 -6.857,31.042 -6.857,44.288c0.004,13.983 5.147,27.483 14.452,37.929c9.305,10.446 22.125,17.117 36.024,18.737c7.069,0.855 13.522,4.446 17.971,10c4.45,5.559 6.542,12.634 5.827,19.717c-17.287,167.008 -115.242,265.658 -193.492,299.463c-3.373,1.425 -6.997,2.158 -10.659,2.158c-3.662,-0 -7.287,-0.733 -10.66,-2.158Zm-163.989,-299.984c16.767,151.808 104.063,241.3 174.418,272.225c70.528,-30.579 157.997,-119.958 174.534,-272.225c-20.627,-2.954 -39.491,-13.262 -53.118,-29.016c-13.626,-15.759 -21.099,-35.905 -21.04,-56.734c-0,-9.904 2.189,-40.312 9.219,-56.033c-34.058,-16.813 -71.547,-25.521 -109.534,-25.442c-37.986,0.079 -75.439,8.946 -109.425,25.904c6.856,15.434 9.046,45.725 9.046,55.571c0.06,20.821 -7.404,40.958 -21.019,56.717c-13.615,15.754 -32.465,26.067 -53.081,29.033Z"
              css={css`
                fill: #79c3ba;
                fill-opacity: 0.3;
                fill-rule: nonzero;
              `}
            />
            <path
              d="M208.933,900.579c-55.085,-29.887 -117.892,-99.917 -137.944,-212.387c20.936,-9.534 38.701,-24.863 51.191,-44.175c12.489,-19.309 19.18,-41.792 19.279,-64.784c-0.222,-12.191 -1.261,-24.35 -3.111,-36.4c45.642,-16.079 95.393,-16.2 141.114,-0.346c-1.885,12.167 -2.964,24.438 -3.227,36.746c0.093,23.009 6.787,45.509 19.288,64.829c12.501,19.325 30.284,34.659 51.24,44.184c-19.822,112.762 -82.686,182.616 -137.83,212.333Z"
              css={css`
                fill: #79c3ba;
                fill-opacity: 0.3;
                fill-rule: nonzero;
              `}
            />
          </animated.g>
          <animated.path
            id="behind-1"
            style={springs[5]}
            d="M2395.59,940.142c-40.655,-0.013 -80.4,12.025 -114.204,34.591c-33.809,22.567 -60.163,54.642 -75.73,92.18c-15.562,37.533 -19.641,78.837 -11.716,118.691c7.925,39.85 27.495,76.458 56.237,105.192c28.742,28.733 65.363,48.304 105.233,56.233c39.871,7.934 81.2,3.867 118.763,-11.679c37.558,-15.546 69.662,-41.875 92.25,-75.658c22.588,-33.779 34.642,-73.5 34.642,-114.13c-0.046,-54.458 -21.709,-106.67 -60.229,-145.183c-38.526,-38.512 -90.763,-60.175 -145.246,-60.237Zm-106.659,204.154c0.059,-24.433 3.95,-48.7 11.525,-71.929l86.029,-0l0,146.333l-85.454,0c-8.066,-23.975 -12.154,-49.108 -12.1,-74.404Zm189.805,-90.417l-73.755,0l0,-91.046c32.934,22.517 58.571,54.163 73.755,91.046Zm-92.771,-93.983l-0,93.983l-78.767,0c15.971,-38.816 43.563,-71.746 78.996,-94.271l-0.229,0.288Zm-0,277.867l-0,91.508c-34.609,-21.95 -61.767,-53.85 -77.905,-91.508l77.905,-0Zm18.783,-0l72.892,-0c-15.392,35.762 -40.679,66.391 -72.892,88.283l0,-88.283Zm0,-18.775l0,-146.621l81.017,-0c15.271,47.629 14.971,98.887 -0.863,146.333l-80.154,0.288Zm100.55,-146.334l61.829,0c9.846,23.138 14.921,48.025 14.921,73.167c0,25.146 -5.075,50.029 -14.921,73.167l-62.346,-0c14.642,-47.642 14.905,-98.542 0.746,-146.334l-0.229,0Zm61.771,0c-1.325,-3.166 -2.825,-6.275 -4.321,-9.329c1.725,2.708 3.225,5.817 4.55,8.983l-0.229,0.346Zm-4.554,-9.733c-1.496,-3.05 -3.109,-5.758 -4.784,-8.925c1.675,2.533 3.517,5.642 5.017,8.579l-0.233,0.346Zm-4.784,-8.983l-58.771,-0c-13.27,-34.634 -34.745,-65.538 -62.575,-90.071c25.413,5.729 49.355,16.691 70.288,32.187c20.937,15.5 38.404,35.196 51.292,57.825l-0.234,0.059Zm-210.141,-88.746c-26.917,24.379 -47.688,54.766 -60.617,88.687l-53.763,0c12.225,-21.625 28.638,-40.604 48.271,-55.829c19.638,-15.229 42.109,-26.396 66.109,-32.858Zm-119.392,97.904c-1.554,3.108 -2.996,6.217 -4.379,9.387l56.816,0c-14.158,47.788 -13.895,98.688 0.746,146.334l-57.621,-0c1.384,3.166 2.825,6.333 4.38,9.387c-12.734,-25.658 -19.359,-53.912 -19.359,-82.554c0,-28.642 6.625,-56.896 19.359,-82.554l0.058,-0Zm4.958,-9.329c-1.671,2.879 -3.229,5.758 -4.725,8.866c1.496,-2.995 3.054,-6.104 4.725,-8.754l0,-0.112Zm-4.725,174.896c1.496,2.995 3.054,5.758 4.725,8.925c-1.671,-3.05 -3.229,-6.046 -4.725,-9.038l0,0.113Zm4.779,8.925l54.742,-0c13.554,34.495 35.308,65.17 63.383,89.379c-24.75,-6.084 -47.995,-17.163 -68.312,-32.55c-20.313,-15.392 -37.263,-34.767 -49.813,-56.942l0,0.113Zm199.43,90.82c29.062,-24.383 51.562,-55.641 65.454,-90.933l59.754,0c-13.167,23.208 -31.154,43.333 -52.75,59.017c-21.6,15.687 -46.304,26.566 -72.458,31.916Zm134.541,-109.42c-1.325,3.112 -2.821,6.22 -4.321,9.329c1.613,-3.284 3.113,-6.392 4.438,-9.5l-0.117,0.171Zm-4.55,9.733c-1.5,2.996 -3.112,5.758 -4.783,8.925c1.554,-3.108 3.4,-6.104 4.9,-9.096l-0.117,0.171Z"
            css={css`
              fill: #79c3ba;
              fill-opacity: 0.25;
              fill-rule: nonzero;
            `}
          />
          <path
            d="M2277.7,728.904l-2006.19,0c-16.612,0 -32.543,6.596 -44.289,18.338c-11.746,11.737 -18.345,27.662 -18.345,44.262l-0,1416.23c-0,16.6 6.599,32.525 18.345,44.262c11.746,11.742 27.677,18.333 44.289,18.333l769.932,0c-0,0 48.975,151.346 -102.854,229.667l720.262,0c0,0 -171.425,-58.742 -127.342,-229.667l746.188,0c8.237,0.017 16.396,-1.591 24.008,-4.729c7.613,-3.142 14.529,-7.75 20.359,-13.562c5.829,-5.817 10.45,-12.721 13.604,-20.325c3.158,-7.6 4.779,-15.75 4.779,-23.98l-0,-1416.23c-0,-8.229 -1.621,-16.379 -4.779,-23.983c-3.154,-7.6 -7.775,-14.509 -13.604,-20.321c-5.83,-5.817 -12.746,-10.425 -20.359,-13.563c-7.612,-3.137 -15.771,-4.745 -24.008,-4.733Z"
            css={css`
              fill: #2a5263;
              fill-rule: nonzero;
            `}
          />
          <path
            d="M1301.37,2121.35c27.312,38.992 -15.442,81.662 -54.45,54.425c-1.279,-0.933 -2.409,-2.063 -3.342,-3.342c-27.196,-38.987 15.442,-81.662 54.45,-54.42c1.283,0.933 2.408,2.058 3.342,3.337Z"
            css={css`
              fill: #203e4a;
              fill-rule: nonzero;
            `}
          />
          <path
            d="M299.341,754.879l1950.53,0c14.825,0 29.041,5.884 39.521,16.359c10.483,10.479 16.37,24.687 16.37,39.5l0,1200.5l-2062.31,-0l0,-1200.56c0.016,-14.808 5.911,-29 16.391,-39.462c10.481,-10.467 24.688,-16.342 39.502,-16.342Z"
            css={css`
              fill: #fff;
              fill-rule: nonzero;
            `}
          />
          <path
            d="M296.172,754.879l1966.32,0c11.491,0 22.512,4.563 30.637,12.684c8.125,8.12 12.692,19.137 12.692,30.62l-0,61.046l-2062.37,0l0,-51.829c0.046,-13.946 5.621,-27.304 15.504,-37.15c9.882,-9.846 23.266,-15.371 37.22,-15.371Z"
            css={css`
              fill: #ebebeb;
              fill-rule: nonzero;
            `}
          />
          <path
            d="M309.137,823.696c7.987,-0 14.462,-6.471 14.462,-14.454c0,-7.984 -6.475,-14.455 -14.462,-14.455c-7.988,0 -14.463,6.471 -14.463,14.455c-0,7.983 6.475,14.454 14.463,14.454Z"
            css={css`
              fill: #e0e0e0;
              fill-rule: nonzero;
            `}
          />
          <path
            d="M352.295,823.696c7.987,-0 14.463,-6.471 14.463,-14.454c-0,-7.984 -6.476,-14.455 -14.463,-14.455c-7.988,0 -14.463,6.471 -14.463,14.455c-0,7.983 6.475,14.454 14.463,14.454Z"
            css={css`
              fill: #e0e0e0;
              fill-rule: nonzero;
            `}
          />
          <path
            d="M393.378,823.696c7.988,-0 14.463,-6.471 14.463,-14.454c0,-7.984 -6.475,-14.455 -14.463,-14.455c-7.988,0 -14.463,6.471 -14.463,14.455c0,7.983 6.475,14.454 14.463,14.454Z"
            css={css`
              fill: #e0e0e0;
              fill-rule: nonzero;
            `}
          />
          <path
            d="M1919.01,2270.51c0,0.692 -285.512,1.209 -637.575,1.209c-352.066,-0 -637.575,-0.517 -637.575,-1.209c0,-0.691 285.396,-1.208 637.575,-1.208c352.18,-0 637.575,0.517 637.575,1.208Z"
            css={css`
              fill: #263238;
              fill-rule: nonzero;
            `}
          />
          <path
            d="M519.8,1233.1l-103.835,-0c-11.106,-0 -20.109,9 -20.109,20.1l-0,103.775c-0,11.1 9.003,20.1 20.109,20.1l103.835,-0c11.104,-0 20.108,-9 20.108,-20.1l0,-103.775c0,-11.1 -9.004,-20.1 -20.108,-20.1Z"
            css={css`
              fill: #f5f5f5;
              fill-rule: nonzero;
            `}
          />
          <path
            d="M579.263,907.317l-272.432,-0c-6.746,-0 -12.215,5.466 -12.215,12.208l-0,53.213c-0,6.741 5.469,12.208 12.215,12.208l272.432,-0c6.745,-0 12.216,-5.467 12.216,-12.208l0,-53.213c0,-6.742 -5.471,-12.208 -12.216,-12.208Z"
            css={css`
              fill: #f5f5f5;
              fill-rule: nonzero;
            `}
          />
          <path
            d="M2240.01,907.317l-138.117,-0c-6.746,-0 -12.217,5.466 -12.217,12.208l0,53.213c0,6.741 5.471,12.208 12.217,12.208l138.117,-0c6.745,-0 12.216,-5.467 12.216,-12.208l0,-53.213c0,-6.742 -5.471,-12.208 -12.216,-12.208Z"
            css={css`
              fill: #f5f5f5;
              fill-rule: nonzero;
            `}
          />
          <path
            d="M2028.66,907.317l-241.546,-0c-6.745,-0 -12.216,5.466 -12.216,12.208l-0,53.213c-0,6.741 5.471,12.208 12.216,12.208l241.546,-0c6.75,-0 12.217,-5.467 12.217,-12.208l0,-53.213c0,-6.742 -5.467,-12.208 -12.217,-12.208Z"
            css={css`
              fill: #f5f5f5;
              fill-rule: nonzero;
            `}
          />
          <path
            d="M1707.37,907.317l-241.55,-0c-6.746,-0 -12.213,5.466 -12.213,12.208l0,53.213c0,6.741 5.467,12.208 12.213,12.208l241.55,-0c6.746,-0 12.216,-5.467 12.216,-12.208l0,-53.213c0,-6.742 -5.47,-12.208 -12.216,-12.208Z"
            css={css`
              fill: #f5f5f5;
              fill-rule: nonzero;
            `}
          />
          <path
            d="M2241.68,1233.1l-1658.44,-0c-5.855,-0 -10.601,4.746 -10.601,10.6l0,36.508c0,5.854 4.746,10.596 10.601,10.596l1658.44,0c5.859,0 10.604,-4.742 10.604,-10.596l0.001,-36.508c-0.001,-5.854 -4.746,-10.6 -10.605,-10.6Z"
            css={css`
              fill: #f5f5f5;
              fill-rule: nonzero;
            `}
          />
          <path
            d="M2241.68,1319.42l-1658.44,0c-5.855,0 -10.601,4.742 -10.601,10.596l0,36.512c0,5.85 4.746,10.596 10.601,10.596l1658.44,0c5.859,0 10.604,-4.746 10.604,-10.596l0.001,-36.512c-0.001,-5.854 -4.746,-10.596 -10.605,-10.596Z"
            css={css`
              fill: #f5f5f5;
              fill-rule: nonzero;
            `}
          />
          <path
            d="M519.8,1457.52l-103.835,-0c-11.106,-0 -20.109,9 -20.109,20.1l-0,103.775c-0,11.1 9.003,20.1 20.109,20.1l103.835,-0c11.104,-0 20.108,-9 20.108,-20.1l0,-103.775c0,-11.1 -9.004,-20.1 -20.108,-20.1Z"
            css={css`
              fill: #f5f5f5;
              fill-rule: nonzero;
            `}
          />
          <path
            d="M2241.68,1457.52l-1658.44,-0c-5.855,-0 -10.601,4.746 -10.601,10.6l0,36.508c0,5.854 4.746,10.596 10.601,10.596l1658.44,0c5.859,0 10.604,-4.742 10.604,-10.596l0.001,-36.508c-0.001,-5.854 -4.746,-10.6 -10.605,-10.6Z"
            css={css`
              fill: #f5f5f5;
              fill-rule: nonzero;
            `}
          />
          <path
            d="M2241.68,1543.85l-1658.44,0c-5.855,0 -10.601,4.742 -10.601,10.596l0,36.512c0,5.85 4.746,10.596 10.601,10.596l1658.44,0c5.859,0 10.604,-4.746 10.604,-10.596l0.001,-36.512c-0.001,-5.854 -4.746,-10.596 -10.605,-10.596Z"
            css={css`
              fill: #f5f5f5;
              fill-rule: nonzero;
            `}
          />
          <path
            d="M519.8,1668.65l-103.835,-0c-11.106,-0 -20.109,8.996 -20.109,20.096l-0,103.775c-0,11.1 9.003,20.1 20.109,20.1l103.835,-0c11.104,-0 20.108,-9 20.108,-20.1l0,-103.775c0,-11.1 -9.004,-20.096 -20.108,-20.096Z"
            css={css`
              fill: #f5f5f5;
              fill-rule: nonzero;
            `}
          />
          <path
            d="M2241.68,1668.65l-1658.44,-0c-5.855,-0 -10.601,4.742 -10.601,10.596l0,36.508c0,5.854 4.746,10.6 10.601,10.6l1658.44,0c5.859,0 10.604,-4.746 10.604,-10.6l0.001,-36.508c-0.001,-5.854 -4.746,-10.596 -10.605,-10.596Z"
            css={css`
              fill: #f5f5f5;
              fill-rule: nonzero;
            `}
          />
          <path
            d="M2241.68,1754.97l-1658.44,-0c-5.855,-0 -10.601,4.742 -10.601,10.596l0,36.512c0,5.85 4.746,10.596 10.601,10.596l1658.44,0c5.859,0 10.604,-4.746 10.604,-10.596l0.001,-36.512c-0.001,-5.854 -4.746,-10.596 -10.605,-10.596Z"
            css={css`
              fill: #f5f5f5;
              fill-rule: nonzero;
            `}
          />
          <path
            d="M2147.59,1104.85l-1742.17,-0c-5.283,-0 -9.565,4.279 -9.565,9.558l-0,62.2c-0,5.279 4.282,9.558 9.565,9.558l1742.17,0c5.279,0 9.562,-4.279 9.562,-9.558l0,-62.2c0,-5.279 -4.283,-9.558 -9.562,-9.558Z"
            css={css`
              fill: #f5f5f5;
              fill-rule: nonzero;
            `}
          />
          <path
            d="M411.414,1309.29l16.765,-16.759l29.217,26.663l51.454,-51.425l15.558,16.758l-69.433,66.917l-43.561,-42.154Z"
            css={css`
              fill: #4bafa2;
              fill-rule: nonzero;
            `}
          />
          <path
            d="M435.5,1270.07c-2.146,-1.875 -4.079,-3.979 -5.762,-6.279c-1.967,-2.113 -3.705,-4.429 -5.188,-6.909c4.238,3.938 7.988,8.363 11.179,13.188l-0.229,-0Z"
            css={css`
              fill: #263238;
              fill-rule: nonzero;
            `}
          />
          <path
            d="M457.971,1267.59c-0.817,-6.205 -1.279,-12.455 -1.383,-18.717c-0.45,-6.225 -0.567,-12.475 -0.346,-18.713c0.779,6.209 1.221,12.459 1.325,18.713c0.454,6.229 0.587,12.475 0.404,18.717Z"
            css={css`
              fill: #263238;
              fill-rule: nonzero;
            `}
          />
          <path
            d="M492.658,1256.13c-1.52,2.742 -3.337,5.309 -5.416,7.659c-1.821,2.554 -3.909,4.908 -6.221,7.025c1.504,-2.75 3.325,-5.321 5.417,-7.659c1.82,-2.55 3.908,-4.908 6.22,-7.025Z"
            css={css`
              fill: #263238;
              fill-rule: nonzero;
            `}
          />
          <animated.g id="front-2" style={springs[2]}>
            <path
              d="M2861.22,1658.57l-1159.28,-0c-14.479,-0 -26.217,11.729 -26.217,26.204l0,426.1c0,14.471 11.738,26.204 26.217,26.204l1159.28,0c14.479,0 26.217,-11.733 26.217,-26.204l-0,-426.1c-0,-14.475 -11.738,-26.204 -26.217,-26.204Z"
              css={css`
                fill: #dcefed;
                fill-rule: nonzero;
              `}
            />
            <path
              d="M2788.05,1798.33l-889.092,0c-3.404,0 -6.162,2.759 -6.162,6.163l-0,24.129c-0,3.404 2.758,6.163 6.162,6.163l889.092,-0c3.404,-0 6.166,-2.759 6.166,-6.163l0,-24.129c0,-3.404 -2.762,-6.163 -6.166,-6.163Z"
              css={css`
                fill: #79c3ba;
                fill-rule: nonzero;
              `}
            />
            <path
              d="M2788.05,1879.59l-889.092,-0c-3.404,-0 -6.162,2.758 -6.162,6.162l-0,24.129c-0,3.405 2.758,6.163 6.162,6.163l889.092,-0c3.404,-0 6.166,-2.758 6.166,-6.163l0,-24.129c0,-3.404 -2.762,-6.162 -6.166,-6.162Z"
              css={css`
                fill: #79c3ba;
                fill-rule: nonzero;
              `}
            />
            <path
              d="M2788.05,1960.85l-889.092,0c-3.404,0 -6.162,2.758 -6.162,6.163l-0,24.129c-0,3.404 2.758,6.162 6.162,6.162l889.092,0c3.404,0 6.166,-2.758 6.166,-6.162l0,-24.129c0,-3.405 -2.762,-6.163 -6.166,-6.163Z"
              css={css`
                fill: #79c3ba;
                fill-rule: nonzero;
              `}
            />
            <path
              d="M1813.91,1798.33l-56.987,0c-4.454,0 -8.067,3.613 -8.067,8.063l0,56.958c0,4.45 3.613,8.063 8.067,8.063l56.987,-0c4.454,-0 8.067,-3.613 8.067,-8.063l0,-56.958c0,-4.45 -3.613,-8.063 -8.067,-8.063Z"
              css={css`
                fill: #79c3ba;
                fill-rule: nonzero;
              `}
            />
          </animated.g>
          <animated.g id="front-3" style={springs[1]}>
            <path
              d="M524.004,1649.35l-506.257,0c-9.801,0 -17.747,7.942 -17.747,17.737l-0,665.788c-0,9.8 7.946,17.738 17.747,17.738l506.257,-0c9.804,-0 17.746,-7.938 17.746,-17.738l0,-665.787c0,-9.796 -7.942,-17.738 -17.746,-17.738Z"
              css={css`
                fill: #fafafa;
                fill-rule: nonzero;
              `}
            />
            <path
              d="M458.721,1759.52l-375.689,-0c-18.235,-0 -33.017,14.771 -33.017,32.996c-0,18.225 14.782,33 33.017,33l375.689,-0c18.233,-0 33.017,-14.775 33.017,-33c-0,-18.225 -14.784,-32.996 -33.017,-32.996Z"
              css={css`
                fill: #9bcde2;
                fill-rule: nonzero;
              `}
            />
            <path
              d="M458.721,1897.79l-375.689,-0c-18.235,-0 -33.017,14.77 -33.017,32.995c-0,18.226 14.782,33.001 33.017,33.001l375.689,-0c18.233,-0 33.017,-14.775 33.017,-33c-0,-18.226 -14.784,-32.996 -33.017,-32.996Z"
              css={css`
                fill: #9bcde2;
                fill-rule: nonzero;
              `}
            />
            <path
              d="M458.721,2036.12l-375.689,-0c-18.235,-0 -33.017,14.771 -33.017,32.996c-0,18.225 14.782,33 33.017,33l375.689,-0c18.233,-0 33.017,-14.775 33.017,-33c-0,-18.225 -14.784,-32.996 -33.017,-32.996Z"
              css={css`
                fill: #9bcde2;
                fill-rule: nonzero;
              `}
            />
            <path
              d="M458.721,2174.45l-375.689,0c-18.235,0 -33.017,14.771 -33.017,32.996c-0,18.225 14.782,33 33.017,33l375.689,-0c18.233,-0 33.017,-14.775 33.017,-33c-0,-18.225 -14.784,-32.996 -33.017,-32.996Z"
              css={css`
                fill: #9bcde2;
                fill-rule: nonzero;
              `}
            />
          </animated.g>
          <animated.g id="front-4" style={springs[0]}>
            <path
              d="M541.75,2380.21c73.925,-0.001 133.854,-59.892 133.854,-133.78c0,-73.883 -59.929,-133.779 -133.854,-133.779c-73.925,0 -133.852,59.896 -133.852,133.779c0,73.888 59.927,133.78 133.852,133.78Z"
              css={css`
                fill: #18c6ac;
                fill-rule: nonzero;
              `}
            />
            <path
              d="M455.379,2253.35l22.008,-23.554l45.292,37.546l83.492,-85.921l21.954,22.058l-107.175,107.979l-65.571,-58.108Z"
              css={css`
                fill: #fff;
                fill-rule: nonzero;
              `}
            />
          </animated.g>
          <path
            d="M1672.05,864.242c6.396,-7.488 7.029,-13.825 -7.433,-15.146c-31.925,-2.883 -55.492,-16.817 -59.18,-24.879c-3.687,-8.063 6.396,-15.954 15.042,-11.117c9.925,4.329 20.392,7.292 31.113,8.813c8.562,1.583 17.329,1.7 25.929,0.345c7.433,-1.154 14.004,-2.42 19.825,-3.629c-0,0 33.821,-7.312 48.4,-18.658l71.508,76.362l-33.538,37.838c-5.045,7.037 -11.808,12.671 -19.641,16.371c-7.833,3.696 -16.484,5.337 -25.129,4.762c-28.809,-2.879 -67.475,-23.496 -67.475,-23.496c-5.242,-8.062 -51.513,20.5 -66.438,31.213c-14.921,10.712 -25.983,8.004 -25.408,-2.304c0.404,-6.967 45.287,-40.829 60.271,-51.079c11.604,-7.28 22.387,-15.796 32.154,-25.396Z"
            css={css`
              fill: #ffc19d;
              fill-rule: nonzero;
            `}
          />
          <path
            d="M1713.88,930.583c0,0.459 2.075,1.384 5.763,1.267c4.779,-0.308 9.362,-2.004 13.191,-4.879c3.829,-2.875 6.734,-6.804 8.359,-11.304c1.095,-3.513 0.808,-5.759 0.404,-5.759c-0.404,0 -2.767,8.409 -11.004,14.396c-5.125,3.125 -10.796,5.259 -16.713,6.279Z"
            css={css`
              fill: #ff9c6a;
              fill-rule: nonzero;
            `}
          />
          <path
            d="M1685.53,919.467c0,0.404 2.017,1.208 5.763,0.979c4.6,-0.425 8.979,-2.167 12.616,-5.008c3.667,-2.817 6.43,-6.638 7.95,-11c1.096,-3.4 0.867,-5.759 0.405,-5.759c-0.459,0 -2.88,7.888 -10.6,13.704c-7.721,5.817 -16.134,5.992 -16.134,7.084Z"
            css={css`
              fill: #ff9c6a;
              fill-rule: nonzero;
            `}
          />
          <path
            d="M1608.66,125.141c-2.942,62.715 -79.463,148.58 -136.392,162.459c-29.904,7.314 -64.363,3.859 -88.042,23.957c-40.683,34.554 -16.883,105.906 -44.312,152.093c-16.884,28.45 -50.363,42.388 -69.146,69.108c-31.517,44.863 -10.371,112.642 32.612,146.046c42.988,33.4 100.321,39.792 154.196,38.354c118.583,-3.054 239.992,-40.716 325.675,-124.683c40.333,-39.446 197.813,-161.246 142.9,-449.999"
            css={css`
              fill: #263238;
              fill-rule: nonzero;
            `}
          />
          <path
            d="M1180.54,573.758l450.766,-133.721c21.004,-9.17 43.042,-15.762 65.633,-19.637l152.175,0.979c20.101,0.088 39.976,4.242 58.43,12.209l410.258,136.254c30.367,9.562 51.342,40.716 51.342,76.133c-0,43.713 -31.517,79.129 -70.355,79.129l-189.516,0c0.575,1.784 0.867,2.821 0.867,2.821l-659.013,2.475l-0,-0.288l-251.517,0.58c-38.891,-0 -70.412,-35.763 -70.412,-79.88c-0,-35.933 20.975,-67.379 51.341,-77.054Z"
            css={css`
              fill: #58abce;
              fill-rule: nonzero;
            `}
          />
          <path
            d="M1270.89,622.479c1.358,0.167 2.733,0.167 4.091,0l11.525,-1.325c10.084,-1.266 24.659,-2.879 42.696,-4.604c93.963,-9.125 188.65,-7.887 282.342,3.683c17.979,2.192 32.558,4.205 42.583,5.759l11.525,1.612c1.325,0.271 2.679,0.388 4.033,0.346c-1.241,-0.567 -2.566,-0.933 -3.92,-1.092l-11.525,-2.304c-9.967,-1.958 -24.488,-4.379 -42.467,-6.912c-93.875,-13.046 -189.021,-14.284 -283.204,-3.684c-18.038,2.071 -32.613,4.088 -42.584,5.759l-11.525,1.958c-1.216,0.133 -2.412,0.404 -3.57,0.804Z"
            css={css`
              fill: #263238;
              fill-rule: nonzero;
            `}
          />
          <g opacity="0.6">
            <path
              d="M1376.62,513.004c0.371,0.538 0.796,1.042 1.266,1.496l3.98,4.204c3.57,3.629 8.812,8.925 15.958,15.434c8.592,7.991 17.737,15.362 27.371,22.058c12.779,9.058 27.029,15.842 42.121,20.042c18.529,4.658 37.962,4.279 56.295,-1.096c20.721,-6.388 40.621,-15.184 59.292,-26.204c20.054,-10.655 40.738,-23.034 64.246,-30.059c24.046,-8.208 50.329,-6.871 73.412,3.742c23.738,10.712 43.905,27.008 65.975,38.758c5.417,3.05 11.234,5.354 16.825,7.888c5.646,2.245 11.421,4.17 17.288,5.762c11.287,3.121 22.875,5.05 34.571,5.759c40.912,2.979 81.608,-8.205 115.241,-31.675c12.275,-9.438 23.834,-19.776 34.571,-30.926c8.238,-8.229 17.271,-15.616 26.967,-22.058c8.442,-5.246 17.846,-8.75 27.658,-10.308c-2.596,0.058 -5.183,0.367 -7.721,0.921c-7.3,1.508 -14.279,4.275 -20.629,8.179c-9.975,6.237 -19.25,13.537 -27.654,21.767c-10.912,10.908 -22.579,21.033 -34.921,30.291c-33.283,22.775 -73.354,33.504 -113.571,30.409c-11.525,-0.696 -22.941,-2.625 -34.054,-5.759c-5.725,-1.625 -11.358,-3.546 -16.883,-5.758c-5.471,-2.534 -11.121,-4.779 -16.479,-7.775c-21.667,-11.517 -41.834,-27.929 -66.146,-38.929c-23.908,-10.963 -51.108,-12.346 -76.004,-3.859c-24.029,7.313 -44.771,19.867 -64.767,30.521c-18.429,10.954 -38.05,19.767 -58.483,26.263c-17.829,5.35 -36.763,5.866 -54.854,1.496c-14.867,-3.996 -28.934,-10.5 -41.605,-19.234c-9.641,-6.533 -18.862,-13.666 -27.6,-21.366c-7.262,-6.334 -12.62,-11.517 -16.366,-14.917l-4.204,-3.917c-0.521,-0.746 -1.038,-1.208 -1.096,-1.15Z"
              css={css`
                fill: #fff;
                fill-opacity: 0.6;
                fill-rule: nonzero;
              `}
            />
          </g>
          <g opacity="0.2">
            <path
              d="M2077.12,621.558l-284.938,53.675l32.905,53.613l8.7,0.116l243.333,-107.404Z"
              css={css`
                fill: #58abce;
                fill-opacity: 0.2;
                fill-rule: nonzero;
              `}
            />
          </g>
          <g opacity="0.6">
            <path
              d="M1214.59,565.296c-0.163,1.454 -0.163,2.921 -0,4.375c0.116,4.171 0.521,8.325 1.208,12.437c4.554,25.88 18.296,49.242 38.708,65.8c20.409,16.559 46.113,25.196 72.388,24.329c14.508,-0.887 28.862,-3.495 42.754,-7.774c14.233,-4.03 27.946,-8.467 41.487,-11.23c12.942,-2.795 26.25,-3.479 39.413,-2.016c18.729,2.35 36.704,8.808 52.642,18.912c15.937,10.104 29.446,23.609 39.55,39.542c1.325,2.304 2.537,4.317 3.687,6.162l2.309,4.721c0.591,1.321 1.329,2.575 2.187,3.742c-0.358,-1.409 -0.858,-2.779 -1.5,-4.088c-0.575,-1.437 -1.267,-3.05 -2.071,-4.895c-0.808,-1.842 -2.133,-4.03 -3.4,-6.334c-7.7,-13.367 -17.937,-25.104 -30.137,-34.554c-18.075,-14.367 -39.729,-23.525 -62.634,-26.492c-13.458,-1.575 -27.083,-0.929 -40.333,1.9c-13.829,2.767 -27.658,7.2 -41.833,11.175c-13.592,4.217 -27.634,6.825 -41.834,7.775c-13.208,0.479 -26.391,-1.471 -38.895,-5.758c-21.35,-7.25 -39.838,-21.108 -52.78,-39.567c-8.695,-12.429 -14.775,-26.496 -17.862,-41.35c-2.708,-10.825 -2.65,-16.812 -3.054,-16.812Z"
              css={css`
                fill: #fff;
                fill-opacity: 0.6;
                fill-rule: nonzero;
              `}
            />
          </g>
          <path
            d="M2317.4,715.892c0,-0 -515.133,227.129 -512.483,222.004c2.65,-5.125 -81.763,-154.167 -81.763,-154.167l417.35,-206.112l176.896,138.275Z"
            css={css`
              fill: #58abce;
              fill-rule: nonzero;
            `}
          />
          <path
            d="M2081.15,606.988c-3.654,1.2 -7.2,2.704 -10.604,4.491l-28.35,13.304c-23.854,11.517 -56.7,27.413 -92.883,45.321c-36.188,17.913 -69.146,34.267 -92.484,46.075l-27.829,14.396c-3.496,1.642 -6.846,3.571 -10.025,5.758c3.65,-1.179 7.196,-2.662 10.6,-4.433l28.35,-13.362c23.854,-11.517 56.758,-27.413 92.942,-45.263c36.187,-17.854 69.146,-34.267 92.196,-46.071l27.829,-14.4c3.587,-1.625 7.021,-3.571 10.258,-5.817Z"
            css={css`
              fill: #58abce;
              fill-rule: nonzero;
            `}
          />
          <g opacity="0.6">
            <path
              d="M1967.87,675.921c0.658,5.337 2.279,10.504 4.783,15.262c6.85,13.134 17.571,23.846 30.709,30.696c8.987,4.938 18.72,8.379 28.812,10.192c11.433,1.904 23.096,2.042 34.571,0.404c24.492,-3.167 49.787,-13.358 74.908,-25.167c25.125,-11.804 49.554,-23.033 73.063,-28.795c11.037,-2.759 22.391,-4.055 33.766,-3.859c9.788,0.254 19.492,1.9 28.813,4.896c13.812,4.096 26.246,11.879 35.954,22.517c3.329,4.1 6.446,8.366 9.333,12.787c-1.946,-5.004 -4.733,-9.641 -8.237,-13.708c-9.55,-11.3 -22.121,-19.65 -36.246,-24.071c-9.629,-3.271 -19.683,-5.113 -29.846,-5.471c-11.633,-0.312 -23.258,0.909 -34.575,3.629c-24.025,5.413 -48.517,16.929 -73.867,28.792c-25.354,11.867 -50.133,21.883 -73.987,25.167c-11.229,1.704 -22.65,1.704 -33.879,-0c-9.738,-1.684 -19.15,-4.892 -27.892,-9.5c-12.841,-6.375 -23.525,-16.392 -30.708,-28.796c-2.142,-4.871 -3.971,-9.871 -5.475,-14.975Z"
              css={css`
                fill: #58abce;
                fill-opacity: 0.6;
                fill-rule: nonzero;
              `}
            />
          </g>
          <g opacity="0.6">
            <path
              d="M1749.2,831.646c0,0.692 11.521,-1.213 29.788,-1.5c10.762,-0.171 21.52,0.366 32.212,1.612c13.075,1.613 26.017,4.171 38.721,7.663c12.621,3.704 24.967,8.3 36.933,13.762c9.767,4.53 19.217,9.705 28.292,15.492c15.558,9.904 24.2,17.275 24.604,16.929c0.404,-0.346 -1.842,-2.187 -5.763,-5.758c-5.458,-4.867 -11.233,-9.367 -17.283,-13.475c-8.996,-6.142 -18.433,-11.608 -28.237,-16.354c-12.067,-5.771 -24.596,-10.525 -37.455,-14.225c-12.874,-3.55 -26.016,-6.054 -39.295,-7.488c-10.854,-1.104 -21.771,-1.412 -32.671,-0.921c-7.342,0.3 -14.659,1.088 -21.896,2.363c-2.725,0.279 -5.396,0.917 -7.95,1.9Z"
              css={css`
                fill: #58abce;
                fill-opacity: 0.6;
                fill-rule: nonzero;
              `}
            />
          </g>
          <g opacity="0.2">
            <path
              d="M1685.64,419.308c8.529,23.842 -4.263,49.467 -16.65,71.525c-7.963,15.721 -18.025,30.288 -29.909,43.305c-5.97,6.487 -13.195,11.695 -21.237,15.32c-8.042,3.621 -16.733,5.58 -25.55,5.759c-23.046,-0.804 -43.85,-17.854 -66.954,-16.529c-15.729,0.92 -29.504,10.195 -42.584,19.004c-22.091,16.033 -46.066,29.304 -71.391,39.508c-25.521,10.237 -53.659,12.013 -80.267,5.067c-26.1,-7.888 -49.208,-28.45 -55.546,-54.996"
              css={css`
                fill: #58abce;
                fill-opacity: 0.2;
                fill-rule: nonzero;
              `}
            />
          </g>
          <path
            d="M1683.57,412.74c-1.209,-27.7 -2.709,-52.809 -2.709,-52.809c0,0 -61.941,-7.774 -64.65,-71.007c-2.708,-63.233 3.63,-208.818 3.63,-208.818l114.091,-54.767l122.734,98.823l-19.075,290.479c-1.334,20.159 -10.538,38.984 -25.625,52.426c-15.088,13.441 -34.855,20.425 -55.042,19.446c-19.242,-0.905 -37.446,-8.984 -51.021,-22.638c-13.579,-13.654 -21.546,-31.9 -22.333,-51.135Z"
            css={css`
              fill: #ffc19d;
              fill-rule: nonzero;
            `}
          />
          <path
            d="M1630.73,194.421c0.013,1.553 0.484,3.068 1.35,4.355c0.871,1.288 2.1,2.29 3.538,2.882c1.437,0.591 3.016,0.746 4.541,0.444c1.525,-0.302 2.925,-1.046 4.025,-2.14c1.105,-1.094 1.859,-2.489 2.171,-4.011c0.317,-1.521 0.171,-3.101 -0.408,-4.541c-0.583,-1.44 -1.579,-2.677 -2.859,-3.555c-1.283,-0.878 -2.795,-1.358 -4.35,-1.381c-1.045,-0.008 -2.087,0.192 -3.058,0.588c-0.971,0.396 -1.854,0.981 -2.6,1.719c-0.746,0.739 -1.333,1.617 -1.737,2.585c-0.405,0.969 -0.613,2.007 -0.613,3.055Z"
            css={css`
              fill: #263238;
              fill-rule: nonzero;
            `}
          />
          <path
            d="M1623.07,177.548c0.979,1.036 6.912,-3.917 15.383,-4.262c8.471,-0.346 14.808,3.974 15.675,2.822c0.863,-1.152 -0.575,-2.419 -3.283,-4.435c-3.7,-2.564 -8.138,-3.845 -12.634,-3.648c-4.495,0.198 -8.804,1.864 -12.262,4.743c-2.534,2.245 -3.4,4.319 -2.879,4.78Z"
            css={css`
              fill: #263238;
              fill-rule: nonzero;
            `}
          />
          <path
            d="M1724.77,194.42c-0.092,1.089 0.041,2.185 0.396,3.22c0.35,1.034 0.916,1.984 1.654,2.789c0.741,0.805 1.637,1.448 2.641,1.888c1,0.439 2.08,0.666 3.175,0.666c1.092,0 2.175,-0.227 3.175,-0.666c1,-0.44 1.9,-1.083 2.638,-1.888c0.742,-0.805 1.304,-1.755 1.658,-2.789c0.354,-1.035 0.488,-2.131 0.392,-3.22c0.096,-1.088 -0.038,-2.185 -0.392,-3.219c-0.354,-1.035 -0.916,-1.984 -1.658,-2.79c-0.738,-0.805 -1.638,-1.447 -2.638,-1.887c-1,-0.44 -2.083,-0.667 -3.175,-0.667c-1.095,0 -2.175,0.227 -3.175,0.667c-1.004,0.44 -1.9,1.082 -2.641,1.887c-0.738,0.806 -1.304,1.755 -1.654,2.79c-0.355,1.034 -0.488,2.131 -0.396,3.219Z"
            css={css`
              fill: #263238;
              fill-rule: nonzero;
            `}
          />
          <path
            d="M1717.1,179.16c0.979,1.094 6.913,-3.916 15.384,-4.204c8.47,-0.288 14.808,3.973 15.733,2.822c0.921,-1.152 -0.579,-2.477 -3.346,-4.493c-3.688,-2.56 -8.121,-3.835 -12.612,-3.627c-4.488,0.208 -8.784,1.888 -12.221,4.78c-2.534,2.245 -3.4,4.088 -2.938,4.722Z"
            css={css`
              fill: #263238;
              fill-rule: nonzero;
            `}
          />
          <path
            d="M1685.24,252.125c-4.496,-1.298 -9.146,-1.995 -13.83,-2.073c-2.187,0 -4.262,-0.576 -4.666,-2.131c-0.209,-2.388 0.271,-4.786 1.383,-6.91c1.904,-5.759 3.975,-11.518 6.108,-17.968c8.529,-25.512 14.638,-46.475 13.713,-46.82c-0.921,-0.346 -8.642,20.099 -17.284,45.61l-5.762,18.026c-1.296,2.869 -1.642,6.078 -0.979,9.156c0.325,0.78 0.821,1.479 1.446,2.046c0.629,0.568 1.375,0.99 2.183,1.237c1.208,0.29 2.446,0.425 3.688,0.403c4.67,0.432 9.379,0.238 14,-0.576Z"
            css={css`
              fill: #263238;
              fill-rule: nonzero;
            `}
          />
          <path
            d="M1713.13,260.878c8.642,-5.759 -23.971,14.052 -41.316,13.764c2.933,4.473 7.333,7.786 12.441,9.373c5.109,1.587 10.613,1.347 15.563,-0.677c18.325,-7.544 13.312,-22.46 13.312,-22.46Z"
            css={css`
              fill: #fff;
              fill-rule: nonzero;
            `}
          />
          <path
            d="M1681.03,359.932c29.384,-0.752 57.909,-10.081 82.05,-26.837c0,0 -19.241,46.072 -80.954,42.386l-1.096,-15.549Z"
            css={css`
              fill: #ff9c6a;
              fill-rule: nonzero;
            `}
          />
          <path
            d="M1923.39,186.473c-0.001,54.71 14.579,199.431 -85.221,219.472l3.629,-199.258c-0,0 -96.688,-45.438 -112.996,-140.402l-0.575,0.518c-6.167,14.34 -17.287,45.092 -10.546,70.144l-90.462,22.287l-10.429,9.502c-0,-0 -32.213,-70.547 29.962,-134.586c62.171,-64.039 115.242,-17.622 148.604,-21.653c22.184,-2.649 99.05,-2.131 130.396,130.208l-2.362,43.768Z"
            css={css`
              fill: #263238;
              fill-rule: nonzero;
            `}
          />
          <path
            d="M1839.15,208.876c8.471,-5.183 19.992,-0.921 26.679,6.392c5.313,5.796 8.604,13.158 9.379,20.982c0.775,7.823 -1.012,15.687 -5.091,22.41c-4.075,6.723 -10.226,11.943 -17.525,14.877c-7.296,2.933 -15.35,3.423 -22.95,1.393"
            css={css`
              fill: #ffc19d;
              fill-rule: nonzero;
            `}
          />
          <path
            d="M1841.22,255.983c-0,0.461 2.821,0.922 7.258,0c2.967,-0.731 5.713,-2.157 8.017,-4.159c2.304,-2.003 4.1,-4.525 5.237,-7.358c1.175,-3.363 1.034,-7.048 -0.404,-10.308c-1.154,-2.56 -3.012,-4.738 -5.358,-6.278c-3.975,-2.591 -7.029,-2.419 -7.088,-1.958c-0.058,0.461 2.479,1.325 5.475,4.031c1.921,1.575 3.317,3.695 4.005,6.081c0.691,2.386 0.641,4.924 -0.146,7.28c-0.992,2.31 -2.442,4.395 -4.263,6.128c-1.825,1.732 -3.979,3.076 -6.337,3.95c-3.805,1.555 -6.455,2.073 -6.396,2.591Z"
            css={css`
              fill: #ff9c6a;
              fill-rule: nonzero;
            `}
          />
          <path
            d="M1849.52,268.48c1.758,0.344 3.321,1.329 4.391,2.763c1.071,1.435 1.567,3.216 1.396,4.997c-0.175,1.78 -1.004,3.433 -2.329,4.635c-1.325,1.203 -3.05,1.869 -4.842,1.869c-1.791,-0 -3.516,-0.666 -4.841,-1.869c-1.325,-1.202 -2.155,-2.855 -2.33,-4.635c-0.17,-1.781 0.325,-3.563 1.396,-4.997c1.071,-1.434 2.638,-2.419 4.392,-2.763"
            css={css`
              fill: #ff4f5b;
              fill-rule: nonzero;
            `}
          />
          <path
            d="M1666.28,730.575l0,-103.95c0,0 154.425,32.021 157.246,102.742l-157.246,1.208Z"
            css={css`
              fill: #ffc19d;
              fill-rule: nonzero;
            `}
          />
          <path
            d="M1682.18,401.799c-2.558,32.03 -16.958,61.959 -40.391,83.955c-23.438,21.996 -54.229,34.484 -86.371,35.025c-23.05,0.4 -46.846,-5.012 -67.65,4.317c-18.092,8.062 -29.729,25.629 -44.021,39.392c-16.983,15.995 -37.95,27.145 -60.712,32.291c-22.759,5.146 -46.488,4.096 -68.705,-3.037c-18.725,-6.275 -48.4,-19.696 -51.858,-39.1c-2.65,-14.8 15.442,-29.488 26.1,-40.313c11.229,-10.025 24.092,-18.05 38.033,-23.729c109.938,-51.829 228.063,-89.204 349.413,-95.597"
            css={css`
              fill: #263238;
              fill-rule: nonzero;
            `}
          />
          <animated.g id="front-1" style={springs[3]}>
            <path
              d="M1467.15,823.583l-762.788,0c-33.412,0 -60.5,27.071 -60.5,60.467l0,201.908c0,33.396 27.088,60.467 60.5,60.467l762.788,-0c33.412,-0 60.5,-27.071 60.5,-60.467l-0,-201.908c-0,-33.396 -27.088,-60.467 -60.5,-60.467Z"
              css={css`
                fill: #4bafa2;
                fill-rule: nonzero;
              `}
            />
            <path
              d="M857.225,898.85c8.554,-0.012 16.908,2.554 23.971,7.371c7.616,5.012 13.841,11.867 18.096,19.925c4.541,8.6 6.912,18.179 6.912,27.904c0,9.725 -2.371,19.3 -6.912,27.9c-4.3,8.063 -10.513,14.942 -18.096,20.042c-7.042,4.879 -15.404,7.487 -23.971,7.487l-45.287,0l2.475,-4.433l-0,50.967c0.12,3.92 -1.284,7.741 -3.917,10.654c-1.313,1.425 -2.921,2.55 -4.713,3.296c-1.791,0.745 -3.72,1.091 -5.658,1.02c-1.896,0.054 -3.775,-0.304 -5.517,-1.05c-1.741,-0.745 -3.3,-1.862 -4.566,-3.266c-2.634,-2.913 -4.038,-6.734 -3.921,-10.654l-0,-142.188c-0.046,-1.987 0.312,-3.962 1.054,-5.804c0.746,-1.842 1.854,-3.513 3.271,-4.908c1.383,-1.409 3.046,-2.517 4.879,-3.25c1.838,-0.734 3.804,-1.08 5.779,-1.013l56.121,0Zm0,82.642c3.429,-0.129 6.679,-1.55 9.104,-3.975c3.125,-2.863 5.638,-6.329 7.375,-10.192c1.971,-4.117 2.975,-8.629 2.942,-13.187c0.062,-4.584 -0.946,-9.121 -2.942,-13.246c-1.741,-3.796 -4.25,-7.188 -7.375,-9.963c-2.466,-2.341 -5.704,-3.691 -9.104,-3.804l-45.75,0l2.938,-4.433l-0,62.716l-2.709,-3.916l45.521,-0Z"
              css={css`
                fill: #edf7f6;
                fill-rule: nonzero;
              `}
            />
            <path
              d="M1008.31,898.85c10.875,-0.212 21.655,2.05 31.521,6.625c8.979,4.367 16.859,10.704 23.046,18.542c6.438,8.221 11.267,17.579 14.233,27.583c3.284,10.792 4.917,22.013 4.842,33.288c0.204,14.92 -2.742,29.716 -8.646,43.424c-5.271,12.475 -13.908,23.242 -24.95,31.096c-11.891,7.992 -26.008,12.025 -40.333,11.517l-58.775,-0c-1.984,0.05 -3.963,-0.308 -5.804,-1.05c-1.842,-0.746 -3.513,-1.854 -4.913,-3.267c-1.417,-1.383 -2.533,-3.041 -3.275,-4.875c-0.746,-1.833 -1.1,-3.804 -1.046,-5.779l0,-142.129c-0.041,-1.983 0.321,-3.954 1.067,-5.792c0.742,-1.837 1.85,-3.508 3.254,-4.904c1.404,-1.4 3.079,-2.5 4.921,-3.237c1.842,-0.734 3.812,-1.088 5.796,-1.042l59.062,-0Zm-2.421,143.975c9.134,0.396 18.109,-2.442 25.355,-8.008c6.712,-5.525 11.691,-12.859 14.345,-21.134c3.142,-9.27 4.68,-19.008 4.555,-28.795c0.025,-7.409 -0.825,-14.792 -2.538,-22c-1.567,-6.563 -4.279,-12.801 -8.008,-18.425c-3.654,-5.538 -8.679,-10.038 -14.584,-13.063c-5.904,-3.029 -12.491,-4.479 -19.125,-4.217l-43.1,0l2.48,-2.245l-0,120.533l-1.5,-2.704l42.12,0.058Z"
              css={css`
                fill: #edf7f6;
                fill-rule: nonzero;
              `}
            />
            <path
              d="M1181.58,898.85c8.55,-0.012 16.908,2.554 23.971,7.371c7.617,5.012 13.837,11.867 18.091,19.925c4.542,8.6 6.917,18.179 6.917,27.904c0,9.725 -2.375,19.3 -6.916,27.9c-4.355,8.108 -10.65,15.013 -18.325,20.1c-7.038,4.875 -15.4,7.488 -23.967,7.488l-45.175,-0l2.475,-4.438l-0,50.967c0.121,3.925 -1.283,7.741 -3.917,10.654c-1.312,1.429 -2.921,2.55 -4.712,3.296c-1.792,0.745 -3.721,1.096 -5.659,1.025c-1.895,0.054 -3.775,-0.309 -5.516,-1.054c-1.742,-0.746 -3.3,-1.859 -4.567,-3.267c-2.633,-2.913 -4.037,-6.729 -3.921,-10.654l0,-142.242c-0.046,-1.987 0.313,-3.962 1.054,-5.804c0.742,-1.842 1.855,-3.513 3.267,-4.909c1.388,-1.408 3.05,-2.516 4.883,-3.25c1.838,-0.733 3.805,-1.079 5.78,-1.012l56.237,-0Zm0,82.642c3.429,-0.13 6.679,-1.55 9.104,-3.975c3.125,-2.863 5.634,-6.33 7.375,-10.192c1.946,-4.125 2.946,-8.629 2.938,-13.188c-0.017,-4.345 -0.946,-8.641 -2.721,-12.608c-1.779,-3.967 -4.363,-7.517 -7.592,-10.429c-2.466,-2.338 -5.708,-3.692 -9.104,-3.8l-45.75,-0l2.938,-4.433l-0,62.712l-2.709,-3.917l45.521,-0.17Z"
              css={css`
                fill: #edf7f6;
                fill-rule: nonzero;
              `}
            />
            <path
              d="M1312.26,937.433l-53.125,124.971c-1.004,2.892 -2.85,5.421 -5.3,7.254c-2.25,1.671 -4.979,2.559 -7.779,2.534c-1.816,0.154 -3.637,-0.096 -5.341,-0.734c-1.705,-0.637 -3.246,-1.646 -4.513,-2.95c-2.158,-2.529 -3.288,-5.779 -3.171,-9.1c0.013,-1.583 0.267,-3.154 0.75,-4.662l61.25,-147.084c1.183,-3.099 3.296,-5.754 6.05,-7.604c2.546,-1.745 5.629,-2.541 8.704,-2.245c3.038,0.008 6.005,0.962 8.479,2.725c2.48,1.766 4.346,4.254 5.346,7.125l60.505,143.97c0.754,1.834 1.183,3.78 1.266,5.759c0.054,1.858 -0.258,3.704 -0.925,5.437c-0.662,1.734 -1.667,3.317 -2.946,4.659c-1.279,1.345 -2.812,2.42 -4.512,3.17c-1.7,0.75 -3.529,1.155 -5.388,1.192c-2.904,0.046 -5.733,-0.908 -8.008,-2.708c-2.579,-1.863 -4.579,-4.421 -5.763,-7.371l-52.725,-123.071l7.146,-1.267Zm-45.521,94.967l13.542,-28.333l66.438,-0l4.666,28.333l-84.646,0Z"
              css={css`
                fill: #edf7f6;
                fill-rule: nonzero;
              `}
            />
          </animated.g>
        </svg>
      </div>
    </section>
  )
}
